import type { BannerCloseReason, SubscriptionPlace } from "app/pages/activation/components/AlertsSubscription/types";
import type { SearchTabs } from "app/pages/activation/components/HomeSearch/types";
import type { CreationFlow } from "app/pages/activation/components/Topics/types";
import { allTrackers } from "app/services/track/track";
import type { track as TrackType } from "pro-features/components/WithTrack/src/context";
//TODO delete unused/old trackings
export type HomeTrackingEntity = {
    trackGranularityToggle(granularity: string): void;
    trackSettingsOpen(): void;
    trackGAConnectButton(eventSubName: string): void;
    trackSWButton(): void;
    trackMainMetricsButton(): void;
    trackMainMetricsCloseModal(): void;
    trackManagePlan(): void;
    trackInvite(): void;
    trackTopicFilter(filter: string): void;
    trackTopicSort(sort: string): void;
    trackTopicContextMenu(item: string): void;
    trackTopicOpenDropdown(): void;
    trackTopicStartCreation(isEmpty: boolean): void;
    trackTopicCreate(topics: string, topicsCount: number, from: CreationFlow): void;
    trackTopicCancelCreation(from: CreationFlow): void;
    trackTopicDelete(isTopicsDeleted: boolean): void;
    trackTopicEdit(): void;
    trackSelectTopic(keyword: string, IsSuggested: boolean): void;
    trackRemoveTopicFromCreation(keyword: string, IsSuggested: boolean): void;
    trackAdvancedModalOpen(): void;
    trackAddNewKeyword(inputBy: string): void;
    trackAlertsIconClick(isSubscribed: boolean, isOwner: boolean): void;
    trackAlertsSubscriptionOpenModal(isOwner: boolean): void;
    trackAlertsSubscriptionCloseModal(isOwner: boolean): void;
    trackClickSubscribeButton(place: SubscriptionPlace, isOwner: boolean): void;
    trackClickUnsubscribeButton(place: SubscriptionPlace, isOwner: boolean): void;
    trackClickCloseBanner(reason: BannerCloseReason): void;
    trackTopicClick(): void;
    trackAlertsUnsubscriptionOpenModal(): void;
    trackAlertsUnsubscriptionCloseModal(): void;
    trackSelectActivationFromDD(): void;
    trackCreateNewActivationFromDD(): void;
    trackImportCSVStart(): void;
    trackImportCSVFinish(kwNumber: number): void;
    trackDefaultHomepageSet(): void;
    trackDefaultHomepageRemove(): void;
    trackDefaultHomepageUpdate(): void;
    trackLinkKWListOpen(): void;
    trackLinkKWListSelect(): void;
    trackLinkKWListUnselect(): void;
    trackLinkKWListCreate(topics: string, topicsCount: number, from: CreationFlow): void;
    trackUserCloseSearch(source: SearchTabs): void;
    trackUserClickTrafficEngagementCTA(): void;
    trackUserClickTrafficEngagementTitle(): void;
    trackUserClickTrafficEngagementWidget(): void;
    trackUserClickTrafficShareWidget(): void;
    trackUserClickTrafficShareCTA(): void;
    trackUserClickTrafficShareTitle(): void;
    trackUserClickDownloadPDFTopic(topicsKWCount: number): void;
    trackUserClickQuickLink(link: string): void;
    trackUserClickOnBreadcrumbs(breadcrumb: string): void;
    trackUserClickTopBannerCTA(): void;
    trackUserClickTopBannerClose(): void;
    trackUserClickAddonPopupDisplayed(): void;
    trackUserClickAddonPopupCTA(): void;
    trackUserClickAddonPopupRemindMeLater(): void;
    trackGAWidgetChangeOrder(draggingItem: string, newOrder: string[]): void;
    trackToggleCollapse(state: string): void;
    trackTopicsModalCollision(numberOfInvalid: number): void;
    trackTopicsModalCollisionSubmit(): void;
    trackTopicsModalCollisionSkip(numberOfInvalid: number): void;
    trackSearchItemClick(itemType: "website" | "keyword", itemName: string, state?: string): void;
    trackSearchTabClick(tab: SearchTabs): void;
};

const createHomeTracking = (track: TrackType): HomeTrackingEntity => ({
    trackGranularityToggle(granularity: string) {
        track("time frame toggle", "click", granularity);
    },
    trackSettingsOpen() {
        track("open settings", "open", "");
    },
    trackGAConnectButton(eventSubName) {
        track("Connect GA", "click", `Connect your website/${eventSubName}`);
    },
    trackGAWidgetChangeOrder(draggingItem, newOrder) {
        track("Widget", "drag", "change order/GA widget", { draggingItem, newOrder: newOrder.join(", ") });
    },
    trackSWButton() {
        track("Button", "click", "select data source/activate main metrics", { source: "SW" });
    },
    trackMainMetricsButton() {
        track("Button", "click", "activate main metrics/open modal");
    },
    trackMainMetricsCloseModal() {
        track("Button", "click", "activate main metrics/close modal");
    },
    trackManagePlan() {
        track("Button", "click", "manage plan");
    },
    trackInvite() {
        track("Button", "click", "invite");
    },
    trackTopicFilter(filter: string) {
        track("Button", "click", "filter topics", { filter_type: filter });
    },
    trackTopicSort(sort: string) {
        track("Button", "click", "sort topics", { filter_type: sort });
    },
    trackTopicContextMenu(item: string) {
        track("Button", item, "topics action menu");
    },
    trackTopicOpenDropdown() {
        track("Button", "click", "open add topics module/topics flow");
    },
    trackTopicStartCreation(isEmpty: boolean) {
        track("Button", "click", "start creation topics/topics flow", { state: isEmpty ? "empty state" : "preview" });
    },
    trackTopicCancelCreation(from: CreationFlow) {
        track("Button", "cancel", "create topics/topics flow", { from });
    },
    trackTopicDelete(isTopicsDeleted: boolean) {
        track("Button", "click", "delete topic", { is_kwList_deleted: isTopicsDeleted });
    },
    trackTopicEdit() {
        track("Button", "click", "edit topic");
    },
    trackTopicCreate(topics: string, topicsCount: number, from: CreationFlow) {
        track("Button", "create", "create topics/topics flow", { topics, num_of_topics: topicsCount, from });
    },
    trackSelectTopic(topicName: string, IsSuggested: boolean) {
        track("Button", "click", "select topic to create/topics flow", {
            topicName,
            state: IsSuggested ? "suggested" : "custom",
        });
    },
    trackRemoveTopicFromCreation(topicName: string, IsSuggested: boolean) {
        track("Button", "click", "remove topic from creation", {
            topicName,
            state: IsSuggested ? "suggested" : "custom",
        });
    },
    trackAdvancedModalOpen() {
        track("Button", "click", "create topics/advanced flow");
    },
    trackAddNewKeyword(inputBy) {
        track("Button", "click", "create topics/Add Keyword", { input_by: inputBy });
    },
    trackAlertsIconClick(isSubscribed: boolean) {
        track("Button", "click", "alerts icon", { isSubscribed });
    },
    trackAlertsSubscriptionOpenModal(isOwner: boolean) {
        track("Button", "click", "alerts subscription/open subscribe modal", { is_owner: isOwner });
    },
    trackAlertsSubscriptionCloseModal(isOwner: boolean) {
        track("Button", "click", "alerts subscription/close subscribe modal", { is_owner: isOwner });
    },
    trackClickSubscribeButton(place: SubscriptionPlace, isOwner: boolean) {
        track("Button", "click", "alerts subscription/subscribe", { place, is_owner: isOwner });
    },
    trackClickUnsubscribeButton(place: SubscriptionPlace, isOwner: boolean) {
        track("Button", "click", "alerts subscription/unsubscribe", { place, is_owner: isOwner });
    },
    trackClickCloseBanner(reason: BannerCloseReason) {
        track("Button", "click", "alerts subscription/close banner", { reason });
    },
    trackTopicClick() {
        track("Button", "click", "go to topic report/topics");
    },
    trackAlertsUnsubscriptionOpenModal() {
        track("Button", "click", "alerts subscription/open unsubscribe modal");
    },
    trackAlertsUnsubscriptionCloseModal() {
        track("Button", "click", "alerts subscription/close unsubscribe modal");
    },
    trackSelectActivationFromDD() {
        track("dropdown", "click", "Select project/Projects dropdown");
    },
    trackCreateNewActivationFromDD() {
        track("Button", "click", "Create new project/Projects dropdown");
    },
    trackImportCSVStart() {
        track("Button", "open", "Create topics/Import from CSV");
    },
    trackImportCSVFinish(kwNumber: number) {
        track("Button", "done", "Create topics/Import from CSV", { num_of_kws: kwNumber });
    },
    trackDefaultHomepageSet() {
        track("Button", "click", "Select Project as default homepage/Set new");
    },
    trackDefaultHomepageRemove() {
        track("Button", "click", "Select Project as default homepage/Remove");
    },
    trackDefaultHomepageUpdate() {
        track("Button", "click", "Select Project as default homepage/Replace");
    },
    trackLinkKWListOpen() {
        track("Button", "click/activation homepage", "start import keyword list/topics flow");
    },
    trackLinkKWListSelect() {
        track("Drop Down", "click/activation homepage", "add keyword list/topics flow");
    },
    trackLinkKWListUnselect() {
        track("Drop Down", "click/activation homepage", "remove keyword list/topics flow");
    },
    trackLinkKWListCreate(topics: string, topicsCount: number, from: CreationFlow) {
        track("Drop Down", "click/activation homepage", "link keyword list/topics flow", {
            topics,
            num_of_topics: topicsCount,
            from,
        });
    },
    trackUserCloseSearch(source) {
        track("Search Bar", "click", `close search/${source.toUpperCase()}`);
    },
    trackUserClickTrafficEngagementCTA() {
        track("Button", "click", "benchmarking report/engagement tab/my traffic and engagement cta");
    },
    trackUserClickTrafficEngagementTitle() {
        track("Button", "click", "benchmarking report/engagement tab/my traffic and engagement title");
    },
    trackUserClickTrafficEngagementWidget() {
        track("Button", "click", "benchmarking report/engagement tab/my traffic and engagement widget");
    },
    trackUserClickTrafficShareWidget() {
        track("Button", "click", "benchmarking report/open/my traffic share widget");
    },
    trackUserClickTrafficShareCTA() {
        track("Button", "click", "benchmarking report/open/my traffic share cta");
    },
    trackUserClickTrafficShareTitle() {
        track("Button", "click", "benchmarking report/open/my traffic share title");
    },
    trackUserClickDownloadPDFTopic(topicsKWCount: number) {
        track("Button", "Download Excel", `download topic to excel/topics/${topicsKWCount}`);
    },
    trackUserClickQuickLink(link: string) {
        track("Button", "click", `quick navigation/${link}`);
    },
    trackUserClickOnBreadcrumbs(breadcrumb: string) {
        track("Button", "click", `breadcrumbs navigation/${breadcrumb}`);
    },
    trackUserClickTopBannerCTA() {
        track("internal link", "click", "upper banner/homepage/buy add on");
    },
    trackUserClickTopBannerClose() {
        track("button", "close", "upper banner/homepage/buy add on");
    },
    trackUserClickAddonPopupDisplayed() {
        track("pop up", "show", "add on pop up/users add on");
    },
    trackUserClickAddonPopupCTA() {
        track("internal link", "click", "add on pop up/users add on/buy add on");
    },
    trackUserClickAddonPopupRemindMeLater() {
        track("pop up", "close", "add on pop up/users add on");
    },
    trackToggleCollapse(state: string) {
        track("Button", "click", `${state}/my traffic and engagement widget`);
    },
    trackTopicsModalCollisionSubmit() {
        track("Button", "click", `submit new topic names/topics flow`);
    },
    trackTopicsModalCollision(numberOfInvalid: number) {
        track("Button", "click", `topic name validation/topics flow/${numberOfInvalid}`);
    },
    trackTopicsModalCollisionSkip(numberOfInvalid: number) {
        track("Button", "click", `topic name validation/topics flow/${numberOfInvalid}`);
    },
    trackSearchItemClick(itemType, itemName, state) {
        track("Search Bar", "click", `${state}/${itemType.toUpperCase()}/${itemName}`);
    },
    trackSearchTabClick(tab) {
        track("Search Bar", "click", `query_${tab}`);
    },
});

export default createHomeTracking(allTrackers.dispatchTrackEvent.bind(allTrackers));
